import React, { useState } from 'react';
import DefaultView from 'components/ui/DefaultView';
import isEqual from 'lodash/isEqual';
import {
  Row,
  Col,
  Button,
  Table,
  Alert,
  SelectOption,
  Select,
} from 'components/ui';
import {
  saveJdRecommendations,
  sendRecommendations,
} from '../../services/recommendations';
import UploadCsvFile from '../../components/blocks/UploadCsv/UploadFile';
import {
  jdRecommendationCsvValidationSchema,
  recommendationCsvValidationSchema,
} from 'validations/recommedations';
import { Icon, Tooltip } from 'antd';
import AppRoute from 'components/AppRoute';

/* eslint-disable react/prop-types */

const Recommendations = () => {
  const [recipients, setRecipients] = useState([]);
  const [skills, setSkills] = useState(new Set());
  const [templateId, setTemplateId] = useState(1);
  const [dataValidationPassed, setDataValidationPassed] = useState(true);
  const [alertMessage, setAlertMessage] = useState('');
  const [errors, setErrors] = useState([]);
  const [recommendationType, setRecommendationsType] = useState('');

  const handleUpdateCsvRecipients = csvRecipients => {
    setDataValidationPassed(true);
    setErrors([]);
    const { data = [] } = csvRecipients;
    setRecipients(data);
    const validationSchema =
      recommendationType === 'skills'
        ? recommendationCsvValidationSchema
        : jdRecommendationCsvValidationSchema;
    const { error } = validationSchema.validate(data, {
      abortEarly: false,
    });
    if (error) {
      setDataValidationPassed(false);
      setErrors(error.details);
    }
    if (recommendationType === 'skills') {
      const filteredSkills = [];
      data.forEach(item => {
        if (item.skills && item.skills.length > 0) {
          const skillsArray = [];
          item.skills
            .split(',')
            .forEach(skill => skillsArray.push(Number(skill)));
          filteredSkills.push(skillsArray);
        }
      });
      const uniqueSkills = new Set(filteredSkills.map(JSON.stringify));
      setSkills(uniqueSkills);
    } else if (recommendationType === 'jd') {
      if (error) {
        setDataValidationPassed(false);
        setErrors(error.details);
      }
    }
  };

  const getSkillRecommendationTableColumnNames = [
    {
      title: 'Name',
      ...getDefaultKeyFields(errors, 'name'),
    },
    {
      title: 'Date',
      ...getDefaultKeyFields(errors, 'date'),
    },
    {
      title: 'Email',
      ...getDefaultKeyFields(errors, 'email'),
    },
    {
      title: 'Skill Ids',
      ...getDefaultKeyFields(errors, 'skills'),
    },
    {
      title: 'Company Name',
      ...getDefaultKeyFields(errors, 'company name'),
    },
    {
      title: 'Send Count',
      ...getDefaultKeyFields(errors, 'send count'),
    },
    {
      title: 'Token',
      ...getDefaultKeyFields(errors, 'token'),
    },
    {
      title: 'Template Id',
      ...getDefaultKeyFields(errors, 'template id'),
    },
  ];

  const getJdRecommendationTableColumnNames = [
    {
      title: 'Name',
      ...getDefaultKeyFields(errors, 'name'),
    },
    {
      title: 'Email',
      ...getDefaultKeyFields(errors, 'email'),
    },
    {
      title: 'Job Description',
      ...getDefaultKeyFields(errors, 'job description'),
    },
    {
      title: 'Job name',
      ...getDefaultKeyFields(errors, 'job name'),
    },
    {
      title: 'job url',
      ...getDefaultKeyFields(errors, 'job url'),
    },
    {
      title: 'Company Name',
      ...getDefaultKeyFields(errors, 'company name'),
    },
    {
      title: 'Send Count',
      ...getDefaultKeyFields(errors, 'send count'),
    },
    {
      title: 'Template Id',
      ...getDefaultKeyFields(errors, 'template id'),
    },
  ];

  const handleSendRecommendations = async () => {
    if (recommendationType === 'skills') {
      await sendRecommendations({
        recipients,
        skills: [...skills],
      })
        .then(() => {
          handleCancel();
          setAlertMessage('Recommendations Data Saved Successfully');
          setTimeout(() => {
            resetAlertMessage();
          }, 4000);
        })
        .catch(() => {
          setAlertMessage('Error saving Recommendations Data');
          setTimeout(() => {
            resetAlertMessage();
          }, 4000);
        });
    } else if (recommendationType === 'jd') {
      await saveJdRecommendations(recipients)
        .then(() => {
          handleCancel();
          setAlertMessage('JD Recommendations Data Saved Successfully');
          setTimeout(() => {
            resetAlertMessage();
          }, 4000);
        })
        .catch(error => {
          setAlertMessage(
            `Error saving JD Recommendations Data - ${error.message}`,
          );
          setTimeout(() => {
            resetAlertMessage();
          }, 4000);
        });
    }
  };

  const handleCancel = () => {
    setRecipients([]);
    setTemplateId(Math.random());
  };

  const resetAlertMessage = () => {
    setAlertMessage('');
  };

  const handleSetRecommendationType = type => {
    handleCancel();
    if (type === recommendationType) {
      setRecommendationsType('');
    } else {
      setRecommendationsType(type);
    }
  };

  return (
    <DefaultView>
      <div style={{ width: 600 }}>
        <Select
          helperTxt="Select Recommendation Type"
          name="selectRecommendationType"
          style={{ width: '100%' }}
          value={recommendationType}
          onChange={handleSetRecommendationType}
        >
          <SelectOption value="">Select Rcommendations Type</SelectOption>
          <SelectOption value="jd">
            Job Description Recommendations
          </SelectOption>
          <SelectOption value="skills">
            Skill Based Recommendations
          </SelectOption>
        </Select>
      </div>
      {recommendationType ? (
        <Row gutter={8}>
          <UploadCsvFile
            recommendations
            recommendationsType={recommendationType}
            templateId={templateId}
            onUpdateRecipients={handleUpdateCsvRecipients}
          />
        </Row>
      ) : null}
      <Row>
        {alertMessage.length > 0 ? (
          <Alert
            message={alertMessage}
            showIcon
            type={alertMessage.includes('Error') ? 'error' : 'success'}
          />
        ) : null}
      </Row>
      {recipients.length > 0 ? (
        <>
          <Row>
            {!dataValidationPassed ? (
              <Alert
                message="Some fields are missing or invalid in the csv file. Please check the table below"
                showIcon
                style={{ marginTop: 5, marginBottom: 5 }}
                type="error"
              />
            ) : null}
          </Row>
          <Row>
            <Table
              columns={
                recommendationType === 'jd'
                  ? getJdRecommendationTableColumnNames
                  : getSkillRecommendationTableColumnNames
              }
              dataSource={recipients}
            />
          </Row>
          <Row>
            <Col offset={8} span={3}>
              <Button
                className="send-mail-form__schedule-submit-btn"
                disabled={!dataValidationPassed}
                onClick={handleSendRecommendations}
              >
                Send Recommendations
              </Button>
            </Col>
            <Col offset={3} span={3}>
              <Button
                className="send-mail-form__schedule-submit-btn"
                disabled={false}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Col>
          </Row>
        </>
      ) : null}
    </DefaultView>
  );
};

const getDefaultKeyFields = (errors, fieldName) => ({
  dataIndex: fieldName,
  key: fieldName,
  render: renderCell(errors, fieldName),
});

const defaultRenderCellFunction = data => data;

const renderCell = (
  errors,
  field,
  renderFunction = defaultRenderCellFunction,
) => (data, record, index) => {
  const error = errors.find(e => isEqual(e.path, [index, field]));
  if (error) {
    return (
      <Tooltip title={error.context?.error?.message || error.message}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            color: error ? '#cc0000' : undefined,
          }}
        >
          <Icon
            style={{ marginRight: 5 }}
            theme="filled"
            type="exclamation-circle"
          />
          {renderFunction(data)}
        </div>
      </Tooltip>
    );
  }
  return renderFunction(data);
};

const RecommendationsRouter = () => (
  <AppRoute
    component={Recommendations}
    path="/"
    permission="recommendations.view"
  />
);

export default RecommendationsRouter;
